import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggleComplete, deleteTask, updateTask, clearCompletedTasks } from '../features/tasks/tasksSlice';

const TaskList = () => {
  const tasks = useSelector((state) => state.tasks);
  const dispatch = useDispatch();
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editPriority, setEditPriority] = useState('low');
  const [filter, setFilter] = useState('all');

  const filteredTasks = tasks.filter(task => {
    if (filter === 'completed') return task.completed;
    if (filter === 'pending') return !task.completed;
    if (filter === 'high') return task.priority === 'high';
    if (filter === 'medium') return task.priority === 'medium';
    if (filter === 'low') return task.priority === 'low';
    return true;
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  const startEdit = (task) => {
    setEditId(task.id);
    setEditTitle(task.title);
    setEditDueDate(task.dueDate);
    setEditPriority(task.priority || 'low');
  };

  const handleUpdate = () => {
    if (!editTitle || !editDueDate) return;
    dispatch(updateTask({ id: editId, title: editTitle, dueDate: editDueDate, priority: editPriority }));
    setEditId(null);
    setEditTitle('');
    setEditDueDate('');
    setEditPriority('low');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isOverdue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high': return { 
        color: 'text-red-400 bg-red-900/20 border-red-800/30',
        label: 'High'
      };
      case 'medium': return { 
        color: 'text-orange-400 bg-orange-900/20 border-orange-800/30',
        label: 'Medium'
      };
      case 'low': return { 
        color: 'text-green-400 bg-green-900/20 border-green-800/30',
        label: 'Low'
      };
      default: return { 
        color: 'text-gray-400 bg-gray-900/20 border-gray-800/30',
        label: 'Low'
      };
    }
  };

  const filterButtons = [
    { key: 'all', label: 'All Tasks' },
    { key: 'pending', label: 'Pending' },
    { key: 'completed', label: 'Completed' },
    { key: 'high', label: 'High Priority' },
    { key: 'medium', label: 'Medium Priority' },
    { key: 'low', label: 'Low Priority' }
  ];

  return (
    <div className="min-h-screen p-6 rounded-xl">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-light text-white mb-2">Your Tasks</h1>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-3 mb-8">
          {filterButtons.map((btn) => (
            <button
              key={btn.key}
              onClick={() => setFilter(btn.key)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                filter === btn.key
                  ? 'bg-white/10 text-white border-white/20'
                  : 'text-gray-400 border-gray-700/30 hover:text-gray-300 hover:border-gray-600/50'
              }`}
            >
              {btn.label}
            </button>
          ))}
          
          {/* Clear Completed Button */}
          <button
            onClick={() => dispatch(clearCompletedTasks())}
            className="ml-auto px-6 py-2 bg-red-600/20 text-red-400 border border-red-600/30 rounded-full text-sm font-medium hover:bg-red-600/30 transition-all duration-200"
          >
            Clear Completed
          </button>
        </div>

        {/* Tasks Container */}
        <div className="space-y-4">
          {sortedTasks.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-medium text-gray-400 mb-2">No tasks found</h3>
              <p className="text-gray-500">
                {filter === 'all' ? 'Start by adding your first task!' : `No ${filter} tasks at the moment.`}
              </p>
            </div>
          ) : (
            sortedTasks.map((task, index) => {
              const priorityConfig = getPriorityConfig(task.priority);
              
              return (
                <div
                  key={task.id}
                  className={`group relative bg-black/60 backdrop-blur-sm border border-gray-800/30 rounded-lg p-6 transition-all duration-200 hover:bg-black/70 hover:border-gray-700/40 ${
                    task.completed ? 'opacity-60' : ''
                  }`}
                >
                  {editId === task.id ? (
                    /* Edit Mode */
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="px-4 py-3 bg-gray-900/50 border border-gray-700/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gray-600/50 transition-all duration-200"
                          placeholder="Task title..."
                        />
                        <input
                          type="date"
                          value={editDueDate}
                          onChange={(e) => setEditDueDate(e.target.value)}
                          className="px-4 py-3 bg-gray-900/50 border border-gray-700/30 rounded-lg text-white focus:outline-none focus:border-gray-600/50 transition-all duration-200"
                        />
                      </div>
                      
                      <div className="flex gap-2 mb-4">
                        {['low', 'medium', 'high'].map((priority) => (
                          <button
                            key={priority}
                            type="button"
                            onClick={() => setEditPriority(priority)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
                              editPriority === priority
                                ? priority === 'low'
                                  ? 'bg-green-900/20 border-green-800/30 text-green-400'
                                  : priority === 'medium'
                                  ? 'bg-orange-900/20 border-orange-800/30 text-orange-400'
                                  : 'bg-red-900/20 border-red-800/30 text-red-400'
                                : 'bg-gray-900/30 border-gray-700/30 text-gray-400 hover:border-gray-600/50'
                            }`}
                          >
                            {priority.charAt(0).toUpperCase() + priority.slice(1)}
                          </button>
                        ))}
                      </div>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={handleUpdate}
                          className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all duration-200"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditId(null)}
                          className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-all duration-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Display Mode */
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          {/* Custom Checkbox */}
                          <div className="relative mt-1">
                            <input
                              type="checkbox"
                              checked={task.completed}
                              onChange={() => dispatch(toggleComplete(task.id))}
                              className="sr-only"
                            />
                            <div
                              className={`w-5 h-5 border-2 rounded cursor-pointer transition-all duration-200 flex items-center justify-center ${
                                task.completed
                                  ? 'bg-green-600 border-green-600'
                                  : 'border-gray-500 hover:border-gray-400'
                              }`}
                              onClick={() => dispatch(toggleComplete(task.id))}
                            >
                              {task.completed && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                </svg>
                              )}
                            </div>
                          </div>

                          {/* Task Content */}
                          <div className="flex-1 min-w-0">
                            <h3 className={`text-lg font-medium mb-2 ${
                              task.completed 
                                ? 'line-through text-gray-500' 
                                : 'text-white'
                            }`}>
                              {task.title}
                            </h3>
                            
                            <p className="text-gray-400 text-sm mb-3">
                              {task.description || 'No description provided'}
                            </p>
                            
                            <div className="flex items-center gap-4 text-sm">
                              {/* Due Date */}
                              <span className="text-gray-400">
                                Due on {formatDate(task.dueDate)}
                                {isOverdue(task.dueDate) && !task.completed && (
                                  <span className="text-red-400 ml-2">Overdue</span>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* Priority Badge */}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${priorityConfig.color}`}>
                          {priorityConfig.label}
                        </span>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => startEdit(task)}
                            className="px-3 py-1 text-gray-400 hover:text-white text-sm"
                            title="Edit task"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => dispatch(toggleComplete(task.id))}
                            className="px-3 py-1 text-gray-400 hover:text-white text-sm"
                            title="Toggle complete"
                          >
                            {task.completed ? 'Undo' : 'Complete'}
                          </button>
                          <button
                            onClick={() => dispatch(deleteTask(task.id))}
                            className="px-3 py-1 text-gray-400 hover:text-red-400 text-sm"
                            title="Delete task"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Summary Stats */}
        {tasks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-black/60 backdrop-blur-sm border border-gray-800/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm font-medium">Total Tasks</p>
                  <p className="text-2xl font-light text-white mt-1">{tasks.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-black/60 backdrop-blur-sm border border-gray-800/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm font-medium">Completed</p>
                  <p className="text-2xl font-light text-white mt-1">{tasks.filter(t => t.completed).length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-black/60 backdrop-blur-sm border border-gray-800/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm font-medium">Pending</p>
                  <p className="text-2xl font-light text-white mt-1">{tasks.filter(t => !t.completed).length}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskList;